import { Box, Typography } from '@mui/material';
import type { ReactElement } from 'react';

import type { AdminApi } from '@/admin/adminApi';
import { AUDIT_PATH } from '@/admin/audit/auditRoutes';
import { ReadBlock } from '@/admin/pilotage/ReadBlock';
import { useAdminRead } from '@/admin/pilotage/useAdminRead';
import { Button, Card } from '@/ui/kit';

import { CONSOLE_LABEL, accessLine, formatDay } from './privacyRequestsModel';

/** Fenetre demandee : celle de la maquette (« Registre des acces · 7 j »). */
const ACCESS_DAYS = 7;

const readAccess = (api: AdminApi) => api.accessSummary(ACCESS_DAYS);

/**
 * « Registre des acces » : qui, parmi l'administration, a consulte des comptes
 * tiers sur les sept derniers jours (`access-summary`, BO-08).
 *
 * C'est la contrepartie de l'acces transverse aux donnees : une demande d'acces
 * se repond aussi a la question « qui a lu mes donnees ? ». `actorUserId` nul est
 * la **console** (octroi et retrait de role, joues sans session), jamais un
 * compte inconnu.
 */
export const AccessRegistryCard = ({ refreshToken }: { readonly refreshToken: number }): ReactElement => {
  const { state, reload } = useAdminRead(readAccess, refreshToken);

  return (
    <Card
      title={`Registre des accès · ${String(ACCESS_DAYS)} j`}
      subtitle="Consultations de comptes tiers par l’administration"
      actions={
        <Button size="S" variant="ghost" to={AUDIT_PATH}>
          Journal d’audit
        </Button>
      }
    >
      <ReadBlock state={state} label="Registre des accès" onRetry={reload} skeletonHeight={96}>
        {(summary) =>
          summary.items.length === 0 ? (
            <Typography sx={{ fontSize: 14, color: 'tokens.textMuted' }}>
              Aucun accès enregistré depuis le {formatDay(summary.since)}.
            </Typography>
          ) : (
            <Box component="ul" aria-label="Accès par administrateur" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {summary.items.map((item) => (
                <Box
                  component="li"
                  key={item.actorUserId ?? 'console'}
                  sx={{ py: 1, borderTop: 1, borderColor: 'tokens.border', '&:first-of-type': { borderTop: 0 } }}
                >
                  <Typography component="div" sx={{ fontSize: 14, fontWeight: 700, overflowWrap: 'anywhere' }}>
                    {item.actorUserId === null ? CONSOLE_LABEL : (item.actorEmail ?? 'Compte introuvable')}
                  </Typography>
                  <Typography component="div" sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
                    {accessLine(item)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )
        }
      </ReadBlock>
    </Card>
  );
};
